import React, {useState} from 'react'
import actions from 'redux-auto'
import {connect} from 'react-redux'
import PropTypes from 'prop-types'
import i18n from 'i18next'
import {createTermine, MAX_DAYS_FOR_REPETITION} from 'ag-common/tools/termine'
import Text from '#components/a/Text'
import Error from '#components/a/Error'
import Button from '#components/a/Button'
import Input from '#components/a/form/Input'
import DropdownSelect from '#components/m/DropdownSelect'
import DatePicker, {PickerMode} from '#components/m/DatePicker'
import ControlsGroup from '#components/m/ControlsGroup'
import {TerminAzubiHintBox} from './TerminAzubiHintBox'
import DateRangePicker from '#components/m/DateRangePicker'
import TerminTypeInput from './TerminTypeInput'
import FlyoutAnchor from '../Flyout/FlyoutAnchor'
import {FlyoutPivot} from '../Flyout/FlyoutPivot'
import {FlyoutPosition} from '../Flyout/FlyoutPosition'
import DialogLogic from '../Flyout/DialogLogic'
import {format, endOfDay, startOfDay, differenceInDays, addDays, isBefore, isSameDay} from 'ag-common/facades'

function RepetitionError({error}) {
  if (!error) {
    return null
  }
  return <Error text={error} />
}

RepetitionError.propTypes = {
  error: PropTypes.string,
}

function RepetitionOptions({selectedTerminRepetitionEndDate, onSelectTerminRepetitionEndDate}) {
  const [showDatePicker, setShowDatePicker] = useState(false)
  const onDateUpdated = date => {
    onSelectTerminRepetitionEndDate(date)
    setShowDatePicker(false)
  }
  const repeatEndText = format(selectedTerminRepetitionEndDate, 'dd.MM.yy')
  return (
    <>
      <div className="textWrapper">
        <Text text={i18n.t('termin_repetition_end')} />
      </div>
      <div className="inputDatePickerWrapper">
        <FlyoutAnchor
          flyoutPosition={FlyoutPosition.bottom}
          flyoutPivot={FlyoutPivot.end}
          openSync={showDatePicker}
          flyoutVisibilityChanged={setShowDatePicker}
          flyoutDistance={-10}
          flyoutContent={
            <DatePicker
              className="date-picker"
              startMonth={selectedTerminRepetitionEndDate}
              initialSelectedStart={selectedTerminRepetitionEndDate}
              initialSelectedEnd={selectedTerminRepetitionEndDate}
              pickerMode={PickerMode.start}
              onRangeChanged={onDateUpdated}
            />
          }>
          <Input type="text" required isReadonly defaultValue={repeatEndText} key={repeatEndText} />
        </FlyoutAnchor>
      </div>
    </>
  )
}

RepetitionOptions.propTypes = {
  selectedTerminRepetitionEndDate: PropTypes.object.isRequired,
  onSelectTerminRepetitionEndDate: PropTypes.func.isRequired,
}

class TerminCreateDialog extends React.Component {
  constructor(props) {
    super(props)
    const start = startOfDay(new Date())
    const end = endOfDay(new Date())
    this.TERMIN_REPETITIONS = [
      {value: 'none', interval: 0, label: `${i18n.t('termin_repeat_keine')}`},
      {value: 'weekly', interval: 7, label: `${i18n.t('termin_repeat_woechentlich')}`},
      {value: 'biweekly', interval: 14, label: `${i18n.t('termin_repeat_zweiwoechentlich')}`},
    ]

    this.state = {
      datePickerStart: start,
      datePickerEnd: end,
      subject: '',
      selectedTerminRepetition: this.TERMIN_REPETITIONS[0],
      selectedTerminRepetitionEndDate: end,
      error: undefined,
      conflictExists: DialogLogic.doesConflictExist(start, end, this.props.termine),
      potentialTermineCount: 0,
    }
  }

  onSelectTerminType = subject => {
    this.setState({subject})
  }

  onSelectTerminRepetition = selectedTerminRepetition => {
    this.setState(
      {
        selectedTerminRepetition,
      },
      () => {
        const potentialTermine = this.getPotentiallyCreatedTermine()
        this.setState({
          conflictExists: this.checkForConflicts(potentialTermine),
          potentialTermineCount: potentialTermine.length,
        })
      },
    )
  }

  onSelectTerminRepetitionEndDate = selectedTerminRepetitionEndDate => {
    const {datePickerEnd} = this.state
    this.setState(
      {
        error: selectedTerminRepetitionEndDate < datePickerEnd ? i18n.t('termin_repeat_invalid_end_date') : undefined,
        selectedTerminRepetitionEndDate: selectedTerminRepetitionEndDate,
      },
      () => {
        const potentialTermine = this.getPotentiallyCreatedTermine()
        this.setState({
          conflictExists: this.checkForConflicts(potentialTermine),
          potentialTermineCount: potentialTermine.length,
        })
      },
    )
  }

  onDateRangeChanged = (start, end) => {
    const terminAdditionalDays = differenceInDays(end, start)
    const invalidForRepeat = terminAdditionalDays > MAX_DAYS_FOR_REPETITION
    this.setState(
      state => ({
        datePickerStart: startOfDay(start),
        datePickerEnd: endOfDay(end),
        invalidForRepeat,
        selectedTerminRepetition: invalidForRepeat ? this.TERMIN_REPETITIONS[0] : state.selectedTerminRepetition,
        selectedTerminRepetitionEndDate: addDays(end, 7),
      }),
      () => {
        const potentialTermine = this.getPotentiallyCreatedTermine()
        this.setState({
          conflictExists: this.checkForConflicts(potentialTermine),
          potentialTermineCount: invalidForRepeat ? 0 : potentialTermine.length,
        })
      },
    )
  }

  getPotentiallyCreatedTermine = () => {
    if (
      isBefore(this.state.selectedTerminRepetitionEndDate, this.state.datePickerStart) &&
      !isSameDay(this.state.selectedTerminRepetitionEndDate, this.state.datePickerStart)
    ) {
      return []
    }
    return createTermine(
      this.state.datePickerStart,
      this.state.datePickerEnd,
      this.state.selectedTerminRepetition.interval,
      this.state.selectedTerminRepetitionEndDate,
    )
  }

  handleSubmit = async e => {
    e.preventDefault()
    const {createTermin, toggleView} = this.props
    const {subject} = this.state
    const termine = this.getPotentiallyCreatedTermine().map(({start, end}) => ({
      subject,
      from: format(start, 'yyyy-MM-dd'),
      to: format(end, 'yyyy-MM-dd'),
    }))
    createTermin({termine})
    toggleView()
  }

  repetitionOptionsEnabled = () => this.state.selectedTerminRepetition !== this.TERMIN_REPETITIONS[0]

  checkForConflicts(potentialTermine) {
    let conflicts = {
      ausbilderTerminConflict: false,
      azubiTerminConflict: false,
    }
    potentialTermine.forEach(x => {
      const conflict = DialogLogic.doesConflictExist(x.start, x.end, this.props.termine)
      conflicts.ausbilderTerminConflict = conflicts.ausbilderTerminConflict || conflict.ausbilderTerminConflict
      conflicts.azubiTerminConflict = conflicts.azubiTerminConflict || conflict.azubiTerminConflict
    })
    return conflicts
  }

  render() {
    const {
      datePickerStart,
      datePickerEnd,
      selectedTerminRepetition,
      selectedTerminRepetitionEndDate,
      error,
      conflictExists,
      invalidForRepeat,
      potentialTermineCount,
    } = this.state

    return (
      <form onSubmit={this.handleSubmit} className="terminCreateDialog">
        <TerminTypeInput onTerminTypeChanged={this.onSelectTerminType} />
        <DateRangePicker
          initialStartDate={datePickerStart}
          initialEndDate={datePickerEnd}
          onRangeUpdated={this.onDateRangeChanged}
        />
        <div className="headline">{i18n.t('termin_wiederholung')}</div>
        <DropdownSelect
          options={this.TERMIN_REPETITIONS}
          selectedOption={selectedTerminRepetition}
          handleSelect={e => this.onSelectTerminRepetition(e)}
          isDisabled={invalidForRepeat}
        />
        {this.repetitionOptionsEnabled() && (
          <RepetitionOptions
            selectedTerminRepetitionEndDate={selectedTerminRepetitionEndDate}
            onSelectTerminRepetitionEndDate={this.onSelectTerminRepetitionEndDate}
          />
        )}
        <TerminAzubiHintBox conflictExists={conflictExists} potentialTermineCount={potentialTermineCount} />
        <RepetitionError error={error} />
        <ControlsGroup>
          <Button variant="secondarydark" text={i18n.t('misc_cancel')} event={this.props.toggleView} />
          <Button
            variant="primarydark"
            text={i18n.t('misc_save')}
            disabled={!!error || conflictExists.ausbilderTerminConflict}
            type="submit"
          />
        </ControlsGroup>
      </form>
    )
  }
}

function mapDispatchToProps() {
  return {
    createTermin: actions.versetzungsplan.createTermin,
  }
}

function mapStateToProps(state) {
  return {
    termine: state.versetzungsplan.versetzungsplaene[0].termine,
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TerminCreateDialog)

TerminCreateDialog.propTypes = {
  toggleView: PropTypes.func,
  createTermin: PropTypes.func,
  termine: PropTypes.array,
}
